import { Children, ReactNode, cloneElement, isValidElement } from 'react';

/**
 * Finds a child component by its display name.
 *
 * @param children - The ReactNode containing the child components.
 * @param name - The display name of the child component to find.
 * @returns The child component with the specified display name, or undefined if not found.
 */
export function childByName(children: ReactNode, name: string) {
  const childrenArray = Children.toArray(children);
  return childrenArray.find(
    // @ts-expect-error - We know that the child is a ReactElement and it has a displayName
    (child) => isValidElement(child) && child.type.displayName === name
  );
}

/**
 * Returns the index of a child element with a specific display name.
 *
 * @param children - The ReactNode containing the child elements.
 * @param name - The display name of the child element to search for.
 * @returns The index of the child element with the specified display name, or
 * -1 if not found.
 */
export function getChildIndex(children: ReactNode, name: string) {
  const childrenArray = Children.toArray(children);
  return childrenArray.findIndex(
    // @ts-expect-error - We know that the child is a ReactElement and it has a displayName
    (child) => isValidElement(child) && child.type.displayName === name
  );
}

/**
 * Checks if the given ReactNode has children with the provided list of
 * display names.
 *
 * @param children - The ReactNode to check for specific children.
 * @param list - An array of display names to match against the children's
 * types.
 * @returns A boolean indicating whether the ReactNode has specific children.
 */
export function hasOnlyChildrenOfType(children: ReactNode, list: string[]) {
  const childrenArray = Children.toArray(children);
  return childrenArray.every((child) => {
    // @ts-expect-error - We know that the child is a ReactElement and it has a displayName
    return isValidElement(child) && list.includes(child.type.displayName);
  });
}

/**
 * Checks if the given ReactNode has specific children with the provided list of
 * display names.
 *
 * @param children - The ReactNode to check for specific children.
 * @param list - An array of display names to match against the children's
 * types.
 * @returns A boolean indicating whether the ReactNode has specific children.
 */
export function hasSpecificChildren(children: ReactNode, list: string[]) {
  const childrenArray = Children.toArray(children);
  return (
    childrenArray.length === list.length &&
    childrenArray.every((child) => {
      // @ts-expect-error - We know that the child is a ReactElement and it has a displayName
      return isValidElement(child) && list.includes(child.type.displayName);
    })
  );
}

/**
 * Checks if the children of a React component are ordered and match a given
 * list of display names.
 *
 * @param children - The children of the React component.
 * @param list - The list of display names to match against.
 * @returns A boolean indicating whether the children are ordered and match the
 * list of display names.
 */
export function hasOrderedChildren(children: ReactNode, list: string[]) {
  const childrenArray = Children.toArray(children);
  return (
    childrenArray.length === list.length &&
    childrenArray.every((child, index) => {
      // @ts-expect-error - We know that the child is a ReactElement and it has a displayName
      return isValidElement(child) && list[index] === child.type.displayName;
    })
  );
}

/**
 * Returns an attribution object with the specified name and URL.
 * If the name is not provided, returns undefined.
 * If the URL is not provided, returns an object with only the name property.
 *
 * @param props - The attribution properties.
 * @param props.attributionName - The name of the attribution.
 * @param props.attributionUrl - The URL of the attribution.
 * @returns The attribution object.
 */
export function getAttributionObj(props: {
  attributionName?: string;
  attributionUrl?: string;
}) {
  const { attributionName: name, attributionUrl: url } = props;

  if (!name) {
    return undefined;
  }

  if (!url) {
    return { name };
  }

  return { name, url };
}

/**
 * Recursively searches the children of a React component for 'BrowserMedia' and
 * sets the 'isNested' prop of the nested 'BrowserMedia' components to true.
 *
 * This is used so that the 'BrowserMedia' component renders properly when
 * inside another Block.
 *
 * @param children - The children of the React component.
 * @returns The nested React components.
 */
export function nestBrowserMedia(children: ReactNode) {
  return Children.map(children, (child): ReactNode => {
    if (!isValidElement(child)) {
      return child;
    }

    // @ts-expect-error - We know that the child is a ReactElement and it has a displayName
    if (child.type.displayName === 'BrowserMedia') {
      // @ts-expect-error - isNested is a prop of BrowserMedia
      return cloneElement(child, { isNested: true });
    }

    if (!child.props.children) {
      return child;
    }

    return cloneElement(
      child,
      undefined,
      nestBrowserMedia(child.props.children)
    );
  });
}
